import { evaluationCapabilities } from '../evaluations/EvaluationHub'
import React from 'react'
import {
  Calendar,
  ClipboardList,
  Activity,
  Users,
  Shield,
  Clock,
  Wifi,
  WifiOff,
  LogOut,
  User,
  ChevronDown,
  LayoutDashboard,
} from 'lucide-react'
import { isDemoEnvironment, env } from '../../config/env'
import { useAuth } from '../auth/AuthContext'
import { hasOperation } from '../auth/permissions'
import { offlineIndexedDb } from '../../lib/offline/indexedDb'
import { syncManager } from '../../lib/offline/syncManager'
import { AlertTriangle, RefreshCw } from 'lucide-react'

export type ActiveTab = 'today' | 'calendar' | 'sports' | 'health' | 'team' | 'admin' | 'auth' | 'evaluations' | 'dashboard' | 'dashboard-preview'

interface AppLayoutProps {
  activeTab: ActiveTab
  onSelectTab: (tab: ActiveTab) => void
  children: React.ReactNode
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  activeTab,
  onSelectTab,
  children,
}) => {
  const isOnline = navigator.onLine
  const { user, signOut } = useAuth()
  const isDemo = isDemoEnvironment()
  const [isAccountMenuOpen, setIsAccountMenuOpen] = React.useState(false)
  const accountMenuRef = React.useRef<HTMLDivElement>(null)
  const [pendingFormsCount, setPendingFormsCount] = React.useState<number>(0)
  const isAdmin = Boolean(
    user?.is_system_admin ||
    user?.memberships.some((m) => m.is_active && (m.roles.includes('admin') || m.capabilities?.includes('manage_users')))
  )

  const canSports = user?.memberships.some((m) => m.is_active && (m.roles.includes('athlete') || ['capture_field', 'view_stats', 'manage_playbook'].some((c) => hasOperation(m.roles, m.capabilities || [], c))))
  const canEvaluations = user?.memberships.some(m => m.is_active && (m.roles.includes('athlete') || evaluationCapabilities.some(c => m.capabilities?.includes(c))))
  const canRoster = user?.memberships.some((m) => m.is_active && hasOperation(m.roles, m.capabilities || [], 'manage_roster'))

  // Consultar formularios pendientes para el badge de navegación
  React.useEffect(() => {
    if (!user) {
      setPendingFormsCount(0)
      return
    }
    const teamId = user.memberships.find((m) => m.team_id)?.team_id || '33333333-3333-3333-3333-333333333333'

    import('../health/formsService').then(({ formsService }) => {
      import('../../lib/supabase').then(({ supabase }) => {
        if (!supabase) return
        supabase
          .from('athletes')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle()
          .then(
            ({ data: ath }) => {
              const athId = ath?.id || (user.email === 'demo.player@teamspain.demo' ? 'a1111111-0000-0000-0000-000000000001' : null)
              if (athId) {
                formsService.getAthletePendingObligations(teamId, athId).then((obls) => {
                  setPendingFormsCount(obls.length)
                }).catch(() => {
                  setPendingFormsCount(0)
                })
              } else {
                setPendingFormsCount(0)
              }
            },
            () => {
              setPendingFormsCount(0)
            }
          )
      })
    })
  }, [user])

  // Guardia de operaciones pendientes en logout
  const [logoutModalOpen, setLogoutModalOpen] = React.useState(false)
  const [pendingLogoutCount, setPendingLogoutCount] = React.useState(0)
  const [isSyncingOnLogout, setIsSyncingOnLogout] = React.useState(false)
  const [logoutSyncError, setLogoutSyncError] = React.useState<string | null>(null)
  const [confirmDiscard, setConfirmDiscard] = React.useState(false)

  const handleRequestSignOut = async () => {
    setIsAccountMenuOpen(false)
    try {
      const pending = await offlineIndexedDb.countPendingOperations(undefined, undefined, user?.id)
      if (pending > 0) {
        setPendingLogoutCount(pending)
        setLogoutSyncError(null)
        setConfirmDiscard(false)
        setLogoutModalOpen(true)
        return
      }
    } catch {
      // IndexedDB no accesible, continuar
    }
    await signOut()
  }

  const handleSyncAndSignOut = async () => {
    setIsSyncingOnLogout(true)
    setLogoutSyncError(null)
    try {
      await syncManager.triggerSync('logout_guard', undefined, user?.id)
      const remaining = await offlineIndexedDb.countPendingOperations(undefined, undefined, user?.id)
      if (remaining === 0) {
        setLogoutModalOpen(false)
        await signOut()
      } else {
        setLogoutSyncError(`No se pudieron sincronizar ${remaining} operaciones. Comprueba la conexión o descarta para salir.`)
        setPendingLogoutCount(remaining)
      }
    } catch (err: unknown) {
      setLogoutSyncError(err instanceof Error ? err.message : 'Error al sincronizar.')
    } finally {
      setIsSyncingOnLogout(false)
    }
  }

  const handleDiscardAndSignOut = async () => {
    if (!confirmDiscard) return
    try {
      // Limpiar operaciones de este usuario
      const ops = await offlineIndexedDb.getPendingOutboxOperations(undefined, user?.id)
      for (const op of ops) {
        await offlineIndexedDb.removeOutboxOperation(op.operation_id)
      }
    } catch {
      // ignore
    }
    setLogoutModalOpen(false)
    await signOut()
  }

  // Cerrar menú al hacer clic fuera o presionar Escape
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setIsAccountMenuOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsAccountMenuOpen(false)
      }
    }

    if (isAccountMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isAccountMenuOpen])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
      {/* Banner discreto de Demostración */}
      {isDemo && (
        <div className="demo-banner" role="status" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 1rem' }}>
          <div className="demo-banner-indicator">
            <span className="demo-banner-dot" aria-hidden="true" />
            <span>Demostración · Datos ficticios</span>
          </div>
          <button
            type="button"
            onClick={() => onSelectTab('dashboard-preview')}
            style={{
              background: activeTab === 'dashboard-preview' ? 'var(--brand-red)' : 'rgba(255, 255, 255, 0.15)',
              border: 'none',
              color: '#ffffff',
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span>Propuesta M8C (Preview)</span>
          </button>
        </div>
      )}

      <div className="app-layout">
        {/* Barra lateral de escritorio (Azul Noche) */}
        <aside className="app-sidebar" aria-label="Navegación principal">
          <div className="sidebar-brand">
            <img src="/branding/logo.png" alt="FEFA" className="brand-logo-img" />
            <div className="brand-text-container">
              <span className="brand-text-title">Team Spain</span>
              <span className="brand-text-subtitle">Flag 5v5</span>
            </div>
          </div>

          <nav className="sidebar-nav">
            <button
              type="button"
              className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => onSelectTab('dashboard')}
            >
              <LayoutDashboard size={18} />
              <span>Dashboard</span>
            </button>
            {canEvaluations && <button className={`nav-item ${activeTab==='evaluations'?'active':''}`} onClick={()=>onSelectTab('evaluations')}><Activity size={18}/><span>Evaluaciones</span></button>}
            <button
              type="button"
              className={`nav-item ${activeTab === 'today' ? 'active' : ''}`}
              onClick={() => onSelectTab('today')}
            >
              <Clock size={18} />
              <span>Hoy / Agenda</span>
            </button>

            <button
              type="button"
              className={`nav-item ${activeTab === 'calendar' ? 'active' : ''}`}
              onClick={() => onSelectTab('calendar')}
            >
              <Calendar size={18} />
              <span>Calendario</span>
            </button>

            {canSports && <button
              type="button"
              className={`nav-item ${activeTab === 'sports' ? 'active' : ''}`}
              onClick={() => onSelectTab('sports')}
            >
              <Activity size={18} />
              <span>Captura y Deporte</span>
            </button>}

            <button
              type="button"
              className={`nav-item ${activeTab === 'health' ? 'active' : ''}`}
              onClick={() => onSelectTab('health')}
            >
              <ClipboardList size={18} />
              <span>Formularios y Salud</span>
              {pendingFormsCount > 0 && (
                <span
                  style={{
                    marginLeft: 'auto',
                    backgroundColor: 'var(--brand-yellow)',
                    color: '#0b0f19',
                    borderRadius: '10px',
                    padding: '0.1rem 0.45rem',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                  }}
                >
                  {pendingFormsCount}
                </span>
              )}
            </button>

            {canRoster && <button
              type="button"
              className={`nav-item ${activeTab === 'team' ? 'active' : ''}`}
              onClick={() => onSelectTab('team')}
            >
              <Users size={18} />
              <span>Equipo y Roster</span>
            </button>}

            {isAdmin && (
              <button
                type="button"
                className={`nav-item ${activeTab === 'admin' ? 'active' : ''}`}
                onClick={() => onSelectTab('admin')}
              >
                <Shield size={18} />
                <span>Administración</span>
              </button>
            )}
          </nav>

          {/* Pie de navegación con usuario y opciones */}
          <div style={{ padding: '0.85rem 0.5rem 0', borderTop: '1px solid var(--nav-border)', marginTop: 'auto' }}>
            {user ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--nav-text)' }}>
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--nav-bg-surface)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--nav-text-muted)',
                      border: '1px solid var(--nav-border)',
                    }}
                  >
                    <User size={14} />
                  </div>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                    <div style={{ fontWeight: 600 }}>{user.profile?.full_name || user.email.split('@')[0]}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--nav-text-muted)' }}>{user.email}</div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleRequestSignOut()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    fontSize: '0.75rem',
                    color: 'var(--nav-text-muted)',
                    padding: '0.35rem 0.5rem',
                    borderRadius: 'var(--radius-sm)',
                    transition: 'color 0.12s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--brand-red)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--nav-text-muted)')}
                >
                  <LogOut size={13} />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            ) : null}

            {import.meta.env.DEV && (
              <div style={{ marginTop: '0.65rem', paddingTop: '0.5rem', borderTop: '1px solid var(--nav-border)' }}>
                <button
                  type="button"
                  onClick={() => onSelectTab('auth')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    fontSize: '0.72rem',
                    color: activeTab === 'auth' ? 'var(--brand-yellow)' : 'var(--nav-text-muted)',
                    width: '100%',
                    padding: '0.3rem 0.4rem',
                    borderRadius: 'var(--radius-sm)',
                    backgroundColor: activeTab === 'auth' ? 'var(--nav-bg-surface)' : 'transparent',
                  }}
                >
                  <Shield size={13} />
                  <span>Diagnóstico técnico (Dev)</span>
                </button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.68rem', color: 'var(--nav-text-muted)', marginTop: '0.25rem', padding: '0 0.25rem' }}>
                  <span>{env.appEnv}</span>
                  <span className={`status-dot ${isOnline ? 'online' : 'offline'}`} />
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* Área principal de contenido */}
        <div className="app-main">
          {/* Cabecera superior sticky */}
          <header className="app-header">
            <div className="header-brand-mobile">
              <img src="/branding/logo.png" alt="FEFA" style={{ height: '28px', width: 'auto' }} />
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>Team Spain</span>
            </div>

            <div className="header-team-badge">
              <span className="badge badge-red" style={{ fontSize: '0.7rem' }}>FLAG 5v5</span>
              <span className="header-team-separator" style={{ color: 'var(--surface-border-strong)' }}>|</span>
              <span className="header-team-subtitle" style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Selección Absoluta Masculina</span>
            </div>

            <div className="header-actions">
              <div className="header-status-indicator" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem' }}>
                {isOnline ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: 'var(--status-success-text)' }} title="Conectado a la red">
                    <Wifi size={14} />
                    <span className="status-dot online" />
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: 'var(--status-warning-text)' }} title="Sin conexión">
                    <WifiOff size={14} />
                    <span>Sin conexión</span>
                  </span>
                )}
              </div>

              {/* Menú de cuenta de usuario (accesible en móvil y escritorio) */}
              {user && (
                <div className="account-menu-wrapper" ref={accountMenuRef}>
                  <button
                    type="button"
                    className="account-menu-btn"
                    onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                    aria-label={`Menú de usuario: ${user.profile?.full_name || user.email}`}
                    aria-expanded={isAccountMenuOpen}
                  >
                    <div className="account-avatar-circle">
                      <User size={15} />
                    </div>
                    <span className="account-btn-label">
                      {user.profile?.full_name || user.email.split('@')[0]}
                    </span>
                    <ChevronDown size={14} className={`account-chevron ${isAccountMenuOpen ? 'open' : ''}`} />
                  </button>

                  {isAccountMenuOpen && (
                    <div className="account-dropdown-card" role="menu">
                      <div className="account-dropdown-header">
                        <div className="account-dropdown-name">
                          {user.profile?.full_name || user.email.split('@')[0]}
                        </div>
                        <div className="account-dropdown-email">{user.email}</div>
                        <div className="account-dropdown-roles">
                          {user.memberships.flatMap((m) => m.roles).map((role, idx) => (
                            <span key={`${role}-${idx}`} className="badge badge-muted">
                              {role === 'coach'
                                ? 'Entrenador'
                                : role === 'medic'
                                ? 'Sanitario'
                                : role === 'athlete'
                                ? 'Jugador'
                                : role === 'trainer'
                                ? 'Preparador'
                                : role === 'admin'
                                ? 'Administrador'
                                : role}
                            </span>
                          ))}
                          {user.profile?.is_system_admin && (
                            <span className="badge badge-yellow">Admin Técnico</span>
                          )}
                        </div>
                      </div>

                      <div className="account-dropdown-body">
                        <button
                          type="button"
                          className="account-logout-btn"
                          style={{ borderBottom: '1px solid var(--surface-border)', color: 'var(--text-main)', marginBottom: '0.25rem' }}
                          onClick={() => {
                            setIsAccountMenuOpen(false)
                            onSelectTab('dashboard-preview')
                          }}
                        >
                          <Activity size={16} style={{ color: 'var(--brand-red)' }} />
                          <span>Propuesta M8C (Preview)</span>
                        </button>
                        {isAdmin && (
                          <button
                            type="button"
                            className="account-logout-btn"
                            style={{ borderBottom: '1px solid var(--surface-border)', color: 'var(--text-main)', marginBottom: '0.25rem' }}
                            onClick={() => {
                              setIsAccountMenuOpen(false)
                              onSelectTab('admin')
                            }}
                          >
                            <Shield size={16} style={{ color: 'var(--brand-red)' }} />
                            <span>Administración</span>
                          </button>
                        )}
                        <button
                          type="button"
                          className="account-logout-btn"
                          onClick={() => handleRequestSignOut()}
                        >
                          <LogOut size={16} />
                          <span>Cerrar sesión</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </header>

          {/* Vista activa */}
          <main className="app-view-container">{children}</main>
        </div>

        {/* Barra de navegación inferior móvil (Azul Noche) */}
        <nav className="app-bottom-nav" aria-label="Navegación móvil">
          <button
            type="button"
            className={`bottom-nav-button ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => onSelectTab('dashboard')}
          >
            <LayoutDashboard size={19} />
            <span>Dashboard</span>
          </button>
          {canEvaluations && <button className={`bottom-nav-button ${activeTab==='evaluations'?'active':''}`} onClick={()=>onSelectTab('evaluations')}><Activity size={19}/><span>Evaluar</span></button>}
          <button
            type="button"
            className={`bottom-nav-button ${activeTab === 'today' ? 'active' : ''}`}
            onClick={() => onSelectTab('today')}
          >
            <Clock size={19} />
            <span>Hoy</span>
          </button>

          <button
            type="button"
            className={`bottom-nav-button ${activeTab === 'calendar' ? 'active' : ''}`}
            onClick={() => onSelectTab('calendar')}
          >
            <Calendar size={19} />
            <span>Agenda</span>
          </button>

          {canSports && <button
            type="button"
            className={`bottom-nav-button ${activeTab === 'sports' ? 'active' : ''}`}
            onClick={() => onSelectTab('sports')}
          >
            <Activity size={19} />
            <span>Campo</span>
          </button>}

          <button
            type="button"
            className={`bottom-nav-button ${activeTab === 'health' ? 'active' : ''}`}
            onClick={() => onSelectTab('health')}
            style={{ position: 'relative' }}
          >
            <ClipboardList size={19} />
            <span>Salud</span>
            {pendingFormsCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '6px',
                  right: '25%',
                  backgroundColor: 'var(--brand-yellow)',
                  color: '#0b0f19',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {pendingFormsCount}
              </span>
            )}
          </button>

          {canRoster && <button
            type="button"
            className={`bottom-nav-button ${activeTab === 'team' ? 'active' : ''}`}
            onClick={() => onSelectTab('team')}
          >
            <Users size={19} />
            <span>Equipo</span>
          </button>}

          {isAdmin && (
            <button
              type="button"
              className={`bottom-nav-button ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => onSelectTab('admin')}
            >
              <Shield size={19} />
              <span>Admin</span>
            </button>
          )}
        </nav>
      </div>

      {/* MODAL DE CONFIRMACIÓN DE LOGOUT CON OPERACIONES PENDIENTES (M5 Requerimiento 8) */}
      {logoutModalOpen && (
        <div
          className="mobile-viewport-overlay"
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(10, 19, 32, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1400,
            padding: '1rem',
          }}
        >
          <div
            className="mobile-viewport-sheet"
            style={{
              backgroundColor: 'var(--surface-card)',
              borderRadius: '12px',
              padding: '1.25rem',
              maxWidth: '460px',
              width: '100%',
              boxShadow: 'var(--shadow-card)',
              border: '1px solid var(--brand-yellow)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.65rem' }}>
              <div
                style={{
                  backgroundColor: 'var(--brand-yellow-subtle)',
                  color: 'var(--brand-yellow)',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AlertTriangle size={18} />
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 800 }}>
                  Operaciones locales sin sincronizar
                </h4>
                <p style={{ margin: 0, fontSize: '0.76rem', color: 'var(--text-secondary)' }}>
                  Hay trabajo pendiente en este dispositivo
                </p>
              </div>
            </div>

            <p style={{ fontSize: '0.82rem', color: 'var(--text-main)', margin: '0 0 0.85rem' }}>
              Tienes <strong>{pendingLogoutCount} operaciones</strong> deportivas guardadas localmente que aún no han sido confirmadas por el servidor.
              Si cierras sesión en otro usuario, no se mezclarán pero podrían quedar inaccesibles en este navegador.
            </p>

            {logoutSyncError && (
              <div
                style={{
                  backgroundColor: 'var(--brand-red-subtle)',
                  color: 'var(--brand-red)',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '6px',
                  fontSize: '0.74rem',
                  marginBottom: '0.85rem',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                }}
              >
                {logoutSyncError}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSyncAndSignOut}
                disabled={isSyncingOnLogout}
                style={{ justifyContent: 'center', fontSize: '0.82rem', padding: '0.55rem' }}
              >
                {isSyncingOnLogout ? (
                  <>
                    <RefreshCw size={14} className="spin" style={{ marginRight: '0.4rem' }} />
                    Sincronizando ahora...
                  </>
                ) : (
                  'Sincronizar ahora y cerrar sesión'
                )}
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setLogoutModalOpen(false)}
                disabled={isSyncingOnLogout}
                style={{ justifyContent: 'center', fontSize: '0.82rem', padding: '0.55rem' }}
              >
                Cancelar y permanecer en la sesión
              </button>
            </div>

            <div
              style={{
                borderTop: '1px solid var(--surface-border)',
                paddingTop: '0.75rem',
                fontSize: '0.74rem',
                color: 'var(--text-secondary)',
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', cursor: 'pointer', marginBottom: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={confirmDiscard}
                  onChange={(e) => setConfirmDiscard(e.target.checked)}
                />
                <span>Entiendo el riesgo y deseo descartar las operaciones locales no sincronizadas</span>
              </label>

              <button
                type="button"
                className="btn btn-outline"
                onClick={handleDiscardAndSignOut}
                disabled={!confirmDiscard || isSyncingOnLogout}
                style={{
                  width: '100%',
                  fontSize: '0.74rem',
                  color: confirmDiscard ? 'var(--brand-red)' : 'var(--text-muted)',
                  borderColor: confirmDiscard ? 'var(--brand-red)' : 'var(--surface-border)',
                }}
              >
                Descartar cambios locales y salir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
